<script lang="ts">
	import { page } from '$app/stores';
	import { tick, onMount } from 'svelte';
	import type { PageData } from '../$types';
	import { type productShopType, type promotionType } from '../../ambient.d';
	import { flavours, baseFlavours } from '$utils/Flavours';
	import { toasts } from '$stores/toasts';
	import { deliveryCharge, deliveryMinimum } from '$utils/common';

	interface PageData {
		status: number;
		products: Array<productShopType>;
		promotions?: Array<promotionType> | undefined;
	}

	export let data: PageData;
	let sortingDetails: HTMLDetailsElement;
	let categoryDetails: HTMLDetailsElement;
	let flavourDetails: HTMLDetailsElement;
	let sortingTypes: { [key: string]: string } = {
		'price-asc': 'Price: Low to High',
		'price-desc': 'Price: High to Low',
		'name-asc': 'Name: A to Z',
		'name-desc': 'Name: Z to A'
	};
	let currentSorting: string = 'name-asc';
	let categoryFilters: Set<string> = new Set();
	let flavourFilters: Set<string> = new Set();
	let currentFilters: { [key: string]: string[] } = {
		categories: [],
		flavours: []
	};
	let productSearchText: string = '';
	let productList: Array<productShopType> = data.products;

	// Reactive statement for currentFilters
	$: if (currentFilters) {
		productList = filterProducts();
		sortProducts();
	}

	// Reactive statement for productSearchText
	$: if (productSearchText !== undefined) {
		productList = filterProducts();
		sortProducts();
	}

	async function createFilterList() {
		if (!productList) return;
		//Resets the category and flavour filters
		categoryFilters = new Set();
		flavourFilters = new Set();
		productList.forEach((product) => {
			const category = product.category as string;
			let productFlavours: string[];

			if (!categoryFilters.has(category)) {
				categoryFilters.add(category);
			}

			if (product.flavour) {
				productFlavours = product.flavour;
			} else if (product.topFlavour) {
				productFlavours = product.topFlavour;
			} else if (product.bottomFlavour) {
				productFlavours = product.bottomFlavour;
			} else if (product.packOneFlavour) {
				productFlavours = product.packOneFlavour;
			} else {
				productFlavours = [];
			}

			let uniqueProductBaseFlavours = new Set<string>();

			// Adds the base flavours to the uniqueProductBaseFlavours set
			productFlavours.forEach((flavour) => {
				for (let baseFlavour of baseFlavours) {
					if (
						flavours[flavour].name
							.toLowerCase()
							.includes(baseFlavour.toLowerCase())
					) {
						flavourFilters.add(baseFlavour);
					}
				}
			});
		});
	}

	onMount(() => {
		if (data) {
			createFilterList();
		}
	});

	$: {
		const newCategories = $page.url.searchParams.getAll('category');
		const uniqueNewCategories = new Set(newCategories);
		if (
			newCategories.length > 0 &&
			!newCategories.every((category) =>
				currentFilters.categories.includes(category)
			) &&
			categoryFilters.size > 0
		) {
			currentFilters.categories = newCategories;
		}
	}

	function filterProducts() {
		let tempProductsList = data.products;
		tempProductsList = tempProductsList.filter((product) => {
			if (
				product.name
					.toLowerCase()
					.includes(productSearchText.toLowerCase().trim()) &&
				(currentFilters.categories.includes(product.category as string) ||
					currentFilters.categories.length < 1) &&
				checkFlavourFilter(product)
			) {
				return product;
			}
		});
		return tempProductsList;
	}

	function checkFlavourFilter(product: productShopType) {
		if (currentFilters.flavours.length < 1) return true;
		let productFlavours: string[];
		if (product.flavour) {
			productFlavours = product.flavour;
		} else if (product.topFlavour) {
			productFlavours = product.topFlavour;
		} else if (product.bottomFlavour) {
			productFlavours = product.bottomFlavour;
		} else if (product.packOneFlavour) {
			productFlavours = product.packOneFlavour;
		} else {
			productFlavours = [];
		}
		for (let flavour of productFlavours) {
			console.log(product.name, flavour);
			for (let baseFlavour of flavours[flavour].baseFlavours) {
				if (currentFilters.flavours.includes(baseFlavour)) {
					return true;
				}
			}
		}
		return false;
	}

	function copyCode(code: string) {
		navigator.clipboard.writeText(code);
		toasts.add({ type: 'success', message: 'Code copied to clipboard' });
	}

	function checkProductsInFilters() {
		return (
			productList.filter((product) => {
				if (
					currentFilters.categories.includes(product.category.toString()) ||
					currentFilters.categories.length < 1
				) {
					return product;
				}
			}).length < 1
		);
	}

	function sortProducts() {
		return productList.sort((a: productShopType, b: productShopType) => {
			if (currentSorting === 'price-asc') {
				if (a.sizes && b.sizes) {
					return a.sizes[0].price - b.sizes[0].price;
				} else if (a.quantity && b.quantity) {
					return a.quantity[0].price - b.quantity[0].price;
				} else if (a.sizes && b.quantity)
					return a.sizes[0].price - b.quantity[0].price;
				else if (a.quantity && b.sizes)
					return a.quantity[0].price - b.sizes[0].price;
			} else if (currentSorting === 'price-desc') {
				if (a.sizes && b.sizes) {
					return b.sizes[0].price - a.sizes[0].price;
				} else if (a.quantity && b.quantity) {
					return b.quantity[0].price - a.quantity[0].price;
				} else if (a.sizes && b.quantity)
					return b.quantity[0].price - a.sizes[0].price;
				else if (a.quantity && b.sizes)
					return b.sizes[0].price - a.quantity[0].price;
			} else if (currentSorting === 'name-asc') {
				return a.name.localeCompare(b.name as string);
			} else if (currentSorting === 'name-desc') {
				return b.name.localeCompare(a.name as string);
			}
			return 0;
		});
	}

	function toggleSortingDetails() {
		sortingDetails.open = !sortingDetails.open;
		if (sortingDetails.open) {
			if (categoryDetails.open) categoryDetails.open = false;
			if (flavourDetails.open) flavourDetails.open = false;
		}
	}

	function toggleCategoryDetails() {
		categoryDetails.open = !categoryDetails.open;
		if (categoryDetails.open) {
			if (sortingDetails.open) sortingDetails.open = false;
			if (flavourDetails.open) flavourDetails.open = false;
		}
	}

	function toggleFlavourDetails() {
		flavourDetails.open = !flavourDetails.open;
		if (flavourDetails.open) {
			if (sortingDetails.open) sortingDetails.open = false;
			if (categoryDetails.open) categoryDetails.open = false;
		}
	}

	$: if (currentSorting) productList = sortProducts();

	function handleCategoryCheckboxChange(event: Event, categoryFilter: string) {
		const target = event.target as HTMLInputElement;
		if (target.checked) {
			currentFilters.categories = [
				categoryFilter,
				...currentFilters.categories
			];
		} else {
			//Remove category from category url params
			if ($page.url.searchParams.has('category')) {
				const currentCategories = $page.url.searchParams.getAll('category');
				$page.url.searchParams.delete('category');
			}
			currentFilters.categories = currentFilters.categories.filter(
				(category) => category !== categoryFilter
			);
		}
	}
</script>

<svelte:head>
	<title>Our online shop</title>
</svelte:head>

<div
	class="bg-logoFontGold px-4 py-3 text-white contrast-more:bg-contrastFontGold"
>
	<h2 class="text-center text-2xl font-bold contrast-more:text-black">
		<span class="fa-duotone fa-bullhorn mr-2"></span>Important Notices
	</h2>
	<p class="text-center text-sm font-medium contrast-more:text-black">
		Delivery is ONLY available for orders over €{deliveryMinimum}. We deliver to
		within the Dublin area. For deliveries outside Dublin, please coordinate
		with Krissy prior to ordering.
	</p>
	<p class="text-center text-sm font-medium contrast-more:text-black">
		Please allow a minimum of 2 days’ notice on all orders. Since we are a small
		bakery, we are unable to make any exceptions. This allows us to maintain our
		high standard in both production and delivery.
	</p>
	<p class="text-center text-sm font-medium contrast-more:text-black">
		There is no such thing as placing an order too early!
	</p>
	<p class="text-center text-sm font-medium contrast-more:text-black">
		All orders are subject to availability.
	</p>
	<p class="text-center text-sm font-medium contrast-more:text-black">
		All items are made in a kitchen that handles nuts and gluten products.
	</p>
</div>
{#if data.promotions && data.promotions.length > 0}
	<aside
		aria-label="Latest promotions"
		class="mx-auto mb-16 w-[90%] rounded-lg p-4 text-center text-xs font-medium uppercase shadow-2xl sm:p-6 lg:p-8"
	>
		{#each data.promotions as promotion}
			<h2 class="text-sm font-semibold tracking-widest">{promotion.name}</h2>
			<p class="mt-6 text-4xl font-black sm:text-5xl lg:text-6xl">
				{promotion.headLine}
			</p>
			{#if promotion.details}
				<p class="mt-2 text-sm font-semibold">{promotion.details}</p>
			{/if}
			{#if promotion.promotionType == 'seasonal product' && promotion.productsIDs && promotion.productsNames}
				<div class="flex flex-col">
					{#each promotion.productsIDs as productID, index}
						<a
							class="inline-block text-sm text-logoBackgroundBlue underline underline-offset-4 transition hover:text-logobackgroundBlueRgba"
							href={'/products/' + productID}
							>{promotion.productsNames[index]}</a
						>
					{/each}
				</div>
			{/if}
			{#if promotion.minimumSpend}
				<p class="mt-2 text-sm font-extrabold">
					When you spend €{promotion.minimumSpend} or more on your next order
				</p>
			{/if}
			{#if promotion.code && promotion.showCode}
				<button
					class="mt-8 inline-block w-full bg-black py-4 text-sm font-bold uppercase tracking-widest text-white"
					on:click={() => copyCode(promotion.code ?? '')}
					>Use code: {promotion.code}<span
						class="fa-duotone fa-copy ml-2"
						aria-label="Click to copy"
					></span>
				</button>
			{/if}
			{#if promotion.end}
				<p class="mt-8 text-xs font-medium text-gray-400">
					Valid until
					{new Date(
						(promotion.end.split('-').length > 2
							? promotion.end
							: new Date().getFullYear() + '-' + promotion.end) + 'T23:59'
					).toLocaleString('en-IE', {
						year: 'numeric',
						month: 'long',
						day: 'numeric',
						hour: 'numeric',
						minute: 'numeric'
					})}*
				</p>
			{/if}
			{#if promotion.remaining}
				<p>Only {promotion.remaining} remaining</p>
			{/if}
		{/each}
	</aside>
{:else}
	<div class="mb-4"></div>
{/if}
{#if data}
	{#if data.products}
		<div class="mx-auto mb-4 flex w-[90%] flex-col md:flex-row">
			<h2 class="sr-only">Filter</h2>
			<div class="mx-auto flex w-[90%] gap-4">
				<div class="relative">
					<input
						class="w-full rounded-md border-gray-200 py-2.5 pe-10 shadow-sm focus:border-logoBackgroundBlue focus:ring-logoBackgroundBlue sm:text-sm"
						type="search"
						placeholder="Search for a product"
						aria-label="Search for a product"
						bind:value={productSearchText}
					/>
					<span
						class="absolute inset-y-0 end-0 grid w-10 place-content-center"
						aria-hidden="true"
					>
						<button aria-label="Search"
							><span
								class="fa-duotone fa-magnifying-glass text-logoBackgroundBlue"
							/></button
						>
					</span>
				</div>
				<p
					aria-live="polite"
					aria-atomic="true"
					class="my-auto h-fit whitespace-nowrap rounded-full bg-logoFontPurple px-2.5 py-2 text-sm text-white md:hidden"
				>
					{#if productList}{productList.length}{:else}0{/if} results
				</p>
			</div>
			<section
				class="mx-auto my-7 flex w-[90%] flex-col space-y-7 md:my-0 md:flex-row md:space-x-7 md:space-y-0"
				aria-label="Filter Controls"
			>
				<details class="group relative" bind:this={sortingDetails}>
					<summary
						on:click|preventDefault={() => toggleSortingDetails()}
						style="-webkit-details-marker: none; list-style-type: none;"
						class="mx-auto w-fit cursor-pointer items-center gap-2 border-b border-gray-700 pb-1 text-gray-900 transition hover:border-gray-400"
					>
						<h3>
							<span class="text-sm font-medium"> Sort</span>
							<span
								class="fa-solid fa-chevron-down text-logoBackgroundBlue transition group-open:-rotate-180"
							>
							</span>
						</h3>
					</summary>
					<fieldset
						aria-label="Sort Options"
						class="flex flex-col items-center justify-between rounded border border-gray-200 bg-white px-3 group-open:mt-2 md:absolute md:z-10"
					>
						<legend class="sr-only">Sort Options</legend>
						{#each Object.keys(sortingTypes) as sortingType}
							<div
								class="flex w-full border-t border-gray-200 px-1 first:border-0"
							>
								<button
									class="text-nowrap px-4 py-2 text-left text-sm text-gray-500 hover:text-logoBackgroundBlue focus:text-logoBackgroundBlue"
									aria-current={currentSorting == sortingType}
									on:click={() => (currentSorting = sortingType)}
								>
									{sortingTypes[sortingType]}
								</button>
								{#if currentSorting == sortingType}
									<span
										class="fa-solid fa-check my-auto text-logoBackgroundBlue"
									/>
								{/if}
							</div>
						{/each}
					</fieldset>
				</details>
				<details class="group relative" bind:this={categoryDetails}>
					<summary
						on:click|preventDefault={() => toggleCategoryDetails()}
						style="-webkit-details-marker: none; list-style-type: none;"
						class="mx-auto w-fit cursor-pointer items-center gap-2 border-b border-gray-700 pb-1 text-gray-900 transition hover:border-gray-400"
					>
						<h3>
							<span class="text-sm font-medium"> Categories</span>
							{#if currentFilters.categories.length > 0}
								<sup
									class="rounded-full bg-logoFontPurple px-1 text-xs text-white"
								>
									{currentFilters.categories.length}
								</sup>
							{/if}
							<span
								class="fa-solid fa-chevron-down text-logoBackgroundBlue transition group-open:-rotate-180"
							>
							</span>
						</h3>
					</summary>
					<fieldset
						aria-label="Category Filters"
						class="flex flex-col items-center justify-between rounded border border-gray-200 bg-white group-open:mt-2 md:absolute md:z-10"
					>
						<legend class="sr-only">Category</legend>
						<div
							class="flex w-full items-center justify-between text-nowrap p-4"
						>
							<span class="text-sm text-gray-700"
								>{currentFilters.categories.length} selected</span
							>
							<button
								class="text-sm text-gray-700 underline underline-offset-4 hover:text-gray-400 focus:text-gray-400"
								type="button"
								on:click={() => (currentFilters.categories = [])}>Clear</button
							>
						</div>
						<ul
							class="w-full space-y-1 whitespace-nowrap border-t border-gray-200 p-4"
						>
							{#each categoryFilters as categoryFilter}
								<li class="flex gap-2">
									<input
										class="size-5 rounded border-gray-300 checked:bg-logoBackgroundBlue checked:text-logobackgroundBlueRgba focus:border-logoBackgroundBlue focus:ring-logoBackgroundBlue"
										checked={currentFilters.categories.includes(categoryFilter)}
										id={categoryFilter.toLowerCase().replace(' ', '-')}
										type="checkbox"
										value={categoryFilter}
										on:change={(event) =>
											handleCategoryCheckboxChange(event, categoryFilter)}
									/>
									<label
										for={categoryFilter.toLowerCase().replace(' ', '-')}
										class="text-sm text-gray-500"
									>
										{#if categoryFilter == 'Layer Cakes'}
											<span
												class="fa-duotone fa-cake-slice text-logoBackgroundBlue"
											/>
										{:else if categoryFilter == 'Cupcakes'}
											<span
												class="fa-duotone fa-cupcake text-logoBackgroundBlue"
											/>
										{:else if categoryFilter == 'Pies'}
											<span class="fa-duotone fa-pie text-logoBackgroundBlue" />
										{:else if categoryFilter == 'Other Desserts'}
											<span
												class="fa-duotone fa-oven text-logoBackgroundBlue"
											/>
										{:else if categoryFilter == 'Sweet Additions'}
											<span
												class="fa-duotone fa-bag-shopping text-logoBackgroundBlue"
											/>
										{/if}
										{categoryFilter}
									</label>
								</li>
							{/each}
						</ul>
					</fieldset>
				</details>
				<details class="group relative" bind:this={flavourDetails}>
					<summary
						on:click|preventDefault={() => toggleFlavourDetails()}
						style="-webkit-details-marker: none; list-style-type: none;"
						class="mx-auto w-fit cursor-pointer items-center gap-2 border-b border-gray-700 pb-1 text-gray-900 transition hover:border-gray-400"
					>
						<h3>
							<span class="text-sm font-medium"> Flavours</span>
							{#if currentFilters.flavours.length > 0}
								<sup
									class="rounded-full bg-logoFontPurple px-1 text-xs text-white"
								>
									{currentFilters.flavours.length}
								</sup>
							{/if}
							<span
								class="fa-solid fa-chevron-down text-logoBackgroundBlue transition group-open:-rotate-180"
							>
							</span>
						</h3>
					</summary>
					<fieldset
						aria-label="Flavour Filters"
						class="flex flex-col items-center justify-between rounded border border-gray-200 bg-white group-open:mt-2 md:absolute md:z-10"
					>
						<legend class="sr-only">Flavour Filters</legend>
						<div
							class="flex w-full items-center justify-between text-nowrap p-4"
						>
							<span class="text-sm text-gray-700"
								>{currentFilters.flavours.length} selected</span
							>
							<button
								class="text-sm text-gray-700 underline underline-offset-4 hover:text-gray-400 focus:text-gray-400"
								type="button"
								on:click={() => (currentFilters.flavours = [])}>Clear</button
							>
						</div>
						<ul
							class="w-full space-y-1 whitespace-nowrap border-t border-gray-200 p-4"
						>
							{#each Array.from(flavourFilters).sort() as flavour}
								<li class="flex gap-2">
									<input
										class="size-5 rounded border-gray-300 checked:bg-logoBackgroundBlue checked:text-logobackgroundBlueRgba focus:border-logoBackgroundBlue focus:ring-logoBackgroundBlue"
										id={flavour.toLowerCase().replace(' ', '-')}
										type="checkbox"
										value={flavour}
										bind:group={currentFilters.flavours}
									/>
									<label
										for={flavour.toLowerCase().replace(' ', '-')}
										class="text-sm text-gray-500"
									>
										{flavour}
									</label>
								</li>
							{/each}
						</ul>
					</fieldset>
				</details>
			</section>
			<p
				aria-live="polite"
				aria-atomic="true"
				class="my-auto hidden h-fit whitespace-nowrap rounded-full bg-logoFontPurple px-2.5 py-2 text-sm text-white md:block"
			>
				{#if productList}{productList.length}{:else}0{/if} results
			</p>
		</div>
		<ul class="mx-auto flex w-[90%] overflow-auto">
			<li
				class="my-auto h-fit whitespace-nowrap rounded-full border border-logoFontPurple px-2.5 py-2 text-sm text-logoFontPurple"
			>
				{sortingTypes[currentSorting]}
			</li>
			{#if currentFilters.categories.length > 0}
				<div class="mx-2"></div>
				{#each currentFilters.categories as category}
					<li
						class="my-auto h-fit whitespace-nowrap rounded-full border border-logoFontPurple px-2.5 py-2 text-sm text-logoFontPurple"
					>
						{category}
					</li>
				{/each}
			{/if}
			{#if currentFilters.flavours.length > 0}
				<div class="mx-2"></div>
				{#each currentFilters.flavours as flavour}
					<li
						class="my-auto h-fit whitespace-nowrap rounded-full border border-logoFontPurple px-2.5 py-2 text-sm text-logoFontPurple"
					>
						{flavour}
					</li>
				{/each}
			{/if}
		</ul>
		<h2 class="sr-only">Our Products</h2>
		{#if productList && categoryFilters}
			{#if checkProductsInFilters()}
				<p>No products found.</p>
			{:else}
				<ul
					aria-label="Product List"
					class="mx-auto my-12 flex w-[90%] flex-col space-y-12 md:grid md:grid-cols-3 md:gap-x-12 md:gap-y-12 md:space-y-0"
				>
					{#each productList as product}
						<li
							class="group flex flex-col justify-end"
							data-category={product.category}
						>
							<a
								class="flex flex-col-reverse"
								href={'/products/' + product._id}
							>
								<div class="mt-3 md:mt-auto">
									<h3
										class="flex flex-col text-2xl font-medium text-gray-900 group-hover:text-gray-600 group-focus:text-gray-600"
									>
										<span>{product.name}</span>
										{#if !product.available}
											<span class="text-red-600">(Temporarily Unavailable)</span
											>
										{/if}
									</h3>
									<p class="mt-1 text-sm text-gray-700">
										{#if product.sizes}
											{#if product.sizes.length > 1}
												€{product.sizes[0].price}-€{product.sizes.slice(-1)[0]
													.price}
											{:else}
												€{product.sizes[0].price}
											{/if}
										{/if}
										{#if product.quantity}
											{#if product.quantity.length > 1}
												€{product.quantity[0].price}-€{product.quantity.slice(
													-1
												)[0].price}
											{:else}
												€{product.quantity[0].price}
											{/if}
										{/if}
									</p>
								</div>

								<img
									class="w-full object-contain"
									data-src={`https://takethecake.gumlet.io/${product._id}/${product.thumbnail.src?.replace(' ', '%20')}`}
									alt={product.thumbnail.alt}
								/>
							</a>
						</li>
					{/each}
				</ul>
			{/if}
		{/if}
	{:else}
		<p>
			Something went wrong when trying to load our products. Please try again.
		</p>
	{/if}
{:else}
	<p>
		Something went wrong when trying to load our products. Please try again.
	</p>
{/if}
